import { Suspense } from 'react';
import { errorMessage } from '@/core/errorMessage';
import { lazyRoute } from './lazyRoute';
import { createBrowserRouter, Navigate, useRouteError, Link } from 'react-router-dom';
import { AppShell, PermissionDenied } from './AppShell';
import { RequireAuth, RequirePlatform, RequireApplicant } from './RequireAuth';
import { RequireSecondFactor } from './RequireSecondFactor';
import { NativeShell } from './NativeShell';
import { LoginPage } from '@/modules/auth/LoginPage';
import { AppearanceSettings } from '@/modules/settings/AppearanceSettings';
import { SettingsLayout } from '@/modules/settings/SettingsLayout';
import { useSession } from '@/core/session';
import { Card, Pill, Button } from '@/components/ui';
import { PageLoading } from '@/components/Loader';
/* Eagerly imported on purpose: it stands in for the dashboard chunk while that
   chunk downloads, so it cannot live inside it. */
import { DashboardSkeleton } from '@/modules/dashboard/DashboardSkeleton';
import { useT } from '@/core/i18n';

// Lazily loaded so the heavy libraries ship in their own chunks. A supervisor's
// phone on the roll-call screen never downloads Recharts or Leaflet.
const DashboardPage = lazyRoute(() => import('@/modules/dashboard/DashboardHome'), 'DashboardHome');
const EmployeesPage = lazyRoute(() => import('@/modules/employees/EmployeesPage'), 'EmployeesPage');
const EmployeeDetailPage = lazyRoute(
  () => import('@/modules/employees/EmployeeDetailPage'),
  'EmployeeDetailPage',
);
const ProjectsPage = lazyRoute(() => import('@/modules/projects/ProjectsPage'), 'ProjectsPage');
const ProjectDetailPage = lazyRoute(
  () => import('@/modules/projects/ProjectsPage'),
  'ProjectDetailPage',
);
const RulesWizard = lazyRoute(() => import('@/modules/settings/RulesWizard'), 'RulesWizard');

/* The console, in its own chunk. Nobody but a handful of operators ever loads
   it, and a thousand site phones should not carry it. */
const PlatformAccount = lazyRoute(
  () => import('@/modules/platform/AccountPage'), 'AccountPage',
);
const PlatformShell = lazyRoute(
  () => import('@/modules/platform/PlatformShell'), 'PlatformShell',
);
const PlatformOverview = lazyRoute(
  () => import('@/modules/platform/OverviewPage'), 'OverviewPage',
);
const CompaniesPage = lazyRoute(
  () => import('@/modules/platform/CompaniesPage'), 'CompaniesPage',
);
const RegistrationsPage = lazyRoute(
  () => import('@/modules/platform/RegistrationsPage'), 'RegistrationsPage',
);
const VisitsPage = lazyRoute(
  () => import('@/modules/platform/VisitsPage'), 'VisitsPage',
);
const ErrorsPage = lazyRoute(
  () => import('@/modules/platform/ErrorsPage'), 'ErrorsPage',
);
const PlatformBackups = lazyRoute(
  () => import('@/modules/platform/BackupsPage'), 'BackupsPage');
const OperatorsPage = lazyRoute(
  () => import('@/modules/platform/OperatorsPage'), 'OperatorsPage',
);
const PlatformSettings = lazyRoute(
  () => import('@/modules/platform/SettingsPage'), 'SettingsPage',
);

/* Joining. Lazily loaded like the console — a thousand site phones sign in to
   a company that already exists and will never render either of these. */
const RegisterPage = lazyRoute(
  () => import('@/modules/onboarding/RegisterPage'), 'RegisterPage',
);
const ApplicationPage = lazyRoute(
  () => import('@/modules/onboarding/ApplicationPage'), 'ApplicationPage',
);
const AttendancePage = lazyRoute(
  () => import('@/modules/attendance/AttendancePage'),
  'AttendancePage',
);
const AttendanceDetailPage = lazyRoute(
  () => import('@/modules/attendance/AttendanceDetailPage'),
  'AttendanceDetailPage',
);
const LeavesPage = lazyRoute(() => import('@/modules/leave/LeavesPage'), 'LeavesPage');
const LeaveDetailPage = lazyRoute(
  () => import('@/modules/leave/LeaveDetailPage'),
  'LeaveDetailPage',
);
const RollcallPage = lazyRoute(() => import('@/modules/rollcall/RollcallPage'), 'RollcallPage');
// The kiosk pulls in TensorFlow. Splitting it out keeps those megabytes off
// every other screen — nobody opening the dashboard should download them.
const FaceKioskPage = lazyRoute(() => import('@/modules/face/FaceKioskPage'), 'FaceKioskPage');
const FaceEnrolPage = lazyRoute(() => import('@/modules/face/FaceEnrolPage'), 'FaceEnrolPage');
const FaceLookupPage = lazyRoute(() => import('@/modules/face/FaceLookupPage'), 'FaceLookupPage');
const PayrollPage = lazyRoute(() => import('@/modules/payroll/PayrollPage'), 'PayrollPage');
const SalarySummaryPage = lazyRoute(() => import('@/modules/payroll/SalarySummaryPage'), 'SalarySummaryPage');
const AdvancesPage = lazyRoute(() => import('@/modules/advances/AdvancesPage'), 'AdvancesPage');
const FleetPage = lazyRoute(() => import('@/modules/fleet/FleetPage'), 'FleetPage');
const MyPortal = lazyRoute(() => import('@/modules/me/MyPortal'), 'MyPortal');
const DocumentsPage = lazyRoute(() => import('@/modules/documents/DocumentsPage'), 'DocumentsPage');
const ReportsPage = lazyRoute(() => import('@/modules/reports/ReportsPage'), 'ReportsPage');
const CompanySettings = lazyRoute(
  () => import('@/modules/settings/CompanySettings'),
  'CompanySettings',
);
const HolidaySettings = lazyRoute(
  () => import('@/modules/settings/CompanySettings'),
  'HolidaySettings',
);
const BranchSettings = lazyRoute(
  () => import('@/modules/settings/BranchSettings'),
  'BranchSettings',
);
const SecuritySettings = lazyRoute(
  () => import('@/modules/settings/SecuritySettings'), 'SecuritySettings');
const BackupSettings = lazyRoute(
  () => import('@/modules/settings/BackupSettings'), 'BackupSettings');
const ResetPasswordPage = lazyRoute(
  () => import('@/modules/auth/ResetPasswordPage'), 'ResetPasswordPage');
const LoginLog = lazyRoute(() => import('@/modules/settings/LoginLog'), 'LoginLog');
const UnclosedShifts = lazyRoute(
  () => import('@/modules/attendance/UnclosedShifts'),
  'UnclosedShifts',
);
const PermanentDelete = lazyRoute(
  () => import('@/modules/settings/PermanentDelete'),
  'PermanentDelete',
);
const SelfCheck = lazyRoute(() => import('@/modules/settings/SelfCheck'), 'SelfCheck');
const ImportPage = lazyRoute(() => import('@/modules/settings/ImportPage'), 'ImportPage');
const NotificationSettings = lazyRoute(
  () => import('@/modules/settings/NotificationSettings'),
  'NotificationSettings',
);
const ActivityLog = lazyRoute(() => import('@/modules/settings/ActivityLog'), 'ActivityLog');
const EmailSettings = lazyRoute(() => import('@/modules/settings/EmailSettings'), 'EmailSettings');
const UserSettings = lazyRoute(() => import('@/modules/settings/UserSettings'), 'UserSettings');

function Lazy({ children, fallback }: {
  children: React.ReactNode;
  /**
   * A skeleton shaped like the screen behind it.
   *
   * PageLoading is the generic one — a heading, four tiles, a slab — and it is
   * the right answer for most screens. It is the wrong answer for a screen that
   * looks nothing like that, because the reader then watches the page assemble
   * itself twice: once as the generic shape and once as itself.
   */
  fallback?: React.ReactNode;
}) {
  return <Suspense fallback={fallback ?? <PageLoading />}>{children}</Suspense>;
}

/**
 * Which shell a signed-in user gets.
 *
 * Somebody with no grants at all is a worker, not an administrator with an
 * empty menu — so they get the self-service portal rather than a dashboard
 * where every query returns nothing.
 *
 * A driver is the same argument one step along, and lands in the same place. He
 * is an employee first — his own attendance, his own payslip, his own leave to
 * request — and marking the crew is one extra thing he does, not the whole of
 * what he is. So he gets the portal too, with a marking tab the others do not
 * have. An admin shell would hand him a sidebar with nothing in it and a
 * dashboard that can read nothing.
 */
function Shell() {
  const { profile, loading } = useSession();
  if (loading) return null;

  const marksOnly =
    profile?.permissions.length === 1 && profile.permissions[0] === 'attendance.mark';

  if (!profile || profile.permissions.length === 0 || marksOnly) {
    return <Lazy><MyPortal /></Lazy>;
  }

  return <AppShell />;
}

/** Route-level guard. The database enforces the same rule independently. */
function Guarded({
  permission, alternative, children,
}: {
  permission: string;
  /** A second grant that also opens the route. Either is enough. */
  alternative?: string;
  children: React.ReactNode;
}) {
  const { can, loading } = useSession();
  if (loading) return null;
  const allowed = can(permission) || (alternative ? can(alternative) : false);
  return allowed ? <>{children}</> : <PermissionDenied />;
}

function RouteError() {
  const t = useT();
  const error = useRouteError() as Error | undefined;
  return (
    <div className="min-h-dvh grid place-items-center p-6 bg-bg">
      <Card className="max-w-md text-center">
        <Pill tone="danger">{t('rt.somethingBroke')}</Pill>
        <h1 className="text-[18px] font-bold mt-3">{t('rt.couldNotShow')}</h1>
        <p className="text-[13px] text-muted mt-2 leading-relaxed">
          {errorMessage(error, 'show this screen')}
        </p>
        <Link to="/" className="inline-block mt-5">
          <Button variant="secondary" size="sm">{t('rt.backToDashboard')}</Button>
        </Link>
      </Card>
    </div>
  );
}

export const router = createBrowserRouter([
  {
    // Everything sits under the native shell so the status bar and the Android
    // back button behave identically on every screen.
    element: <NativeShell />,
    errorElement: <RouteError />,
    children: [
      { path: '/login', element: <LoginPage /> },
      /* Where a reset link lands. Outside every guard on purpose: the person
         arriving has a recovery session and no idea what their password is,
         which is precisely the state the guards exist to turn away. */
      { path: '/reset', element: <Lazy><ResetPasswordPage /></Lazy> },
      { path: '/register', element: <Lazy><RegisterPage /></Lazy> },

      /* The application.
         --------------------------------------------------------------------
         Behind a session but not behind RequireAuth, deliberately: everybody
         who lands here has an account and no company, which is precisely the
         state RequireAuth exists to refuse. */
      {
        path: '/apply',
        element: (
          <RequireApplicant>
            <Lazy><ApplicationPage /></Lazy>
          </RequireApplicant>
        ),
      },

      /* The console.
         --------------------------------------------------------------------
         Outside the company shell entirely, because an operator standing here
         is in no company: no theme, no currency, no permissions, no unread
         count. RequirePlatform turns everybody else away — including a
         company's own super admin, whose `*` grant means nothing at this
         level. */
      {
        path: '/platform',
        element: (
          <RequirePlatform>
            <Lazy><PlatformShell /></Lazy>
          </RequirePlatform>
        ),
        children: [
          { index: true, element: <Lazy><PlatformOverview /></Lazy> },
          { path: 'companies', element: <Lazy><CompaniesPage /></Lazy> },
          { path: 'applications', element: <Lazy><RegistrationsPage /></Lazy> },
          { path: 'visits', element: <Lazy><VisitsPage /></Lazy> },
          { path: 'errors', element: <Lazy><ErrorsPage /></Lazy> },
          { path: 'backups', element: <Lazy><PlatformBackups /></Lazy> },
          { path: 'operators', element: <Lazy><OperatorsPage /></Lazy> },
          { path: 'account', element: <Lazy><PlatformAccount /></Lazy> },
          { path: 'settings', element: <Lazy><PlatformSettings /></Lazy> },
          { path: '*', element: <Navigate to="/platform" replace /> },
        ],
      },

      {
        path: '/',
        element: (
          <RequireAuth>
            {/* Above every route rather than on the login form. A password is
                only one of four ways in — there is a fingerprint unlock, a
                restored session, and a deep link straight to a payslip — and
                guarding the form would cover one of them. */}
            <RequireSecondFactor>
              <Shell />
            </RequireSecondFactor>
          </RequireAuth>
        ),
        children: [
      { index: true, element: <Lazy fallback={<DashboardSkeleton />}><DashboardPage /></Lazy> },

      {
        path: 'employees',
        element: (
          <Guarded permission="employees.view">
            <Lazy><EmployeesPage /></Lazy>
          </Guarded>
        ),
      },
      {
        path: 'employees/:id',
        element: (
          <Guarded permission="employees.view">
            <Lazy><EmployeeDetailPage /></Lazy>
          </Guarded>
        ),
      },
      {
        path: 'attendance',
        element: (
          <Guarded permission="attendance.view">
            <Lazy><AttendancePage /></Lazy>
          </Guarded>
        ),
      },
      {
        // Linked from the notification a man being left open raises, so the
        // path is part of that message and must not move without it.
        path: 'attendance/unclosed',
        element: (
          <Guarded permission="attendance.view">
            <Lazy><UnclosedShifts /></Lazy>
          </Guarded>
        ),
      },
      {
        // One man, one day. Reachable by a worker for his own record too — the
        // function behind it refuses anybody else's.
        path: 'attendance/:employeeId/:workDate',
        element: <Lazy><AttendanceDetailPage /></Lazy>,
      },
      {
        /* The office grant alone, since 0192.
           This is the one screen that can say somebody was present without
           them having proved it, and while the site could reach it the face
           scanning beside it was optional in practice. It is still here and
           still needed — somebody who cannot be scanned has to be marked
           somehow — but it is the office's exception, not the site's habit.

           attendance.view is deliberately not an alternative: it is held by
           supervisors, so admitting it would leave the door open to exactly
           the people the rule is about. */
        path: 'rollcall',
        element: (
          <Guarded permission="attendance.edit">
            <Lazy><RollcallPage /></Lazy>
          </Guarded>
        ),
      },
      {
        // Same permission as roll-call: it is the same act — recording who was
        // on site — done by a camera instead of a supervisor's memory.
        path: 'face',
        element: (
          // A driver may scan; only employees.edit may enrol. Scanning records
          // who was present, enrolling decides whose face the system answers to
          // from then on, and only the second is worth restricting.
          <Guarded permission="attendance.mark" alternative="attendance.view">
            <Lazy><FaceKioskPage /></Lazy>
          </Guarded>
        ),
      },
      {
        /* Enrolling a face is editing an employee record, not taking
           attendance, and was gated on the office grant alone. But the site
           person who takes somebody on at the gate is the one standing in
           front of them, and a worker who cannot be scanned cannot be marked
           at all — so the grant that lets them take somebody on lets them
           enrol that face too. The database agrees separately: 0185 widened
           face_templates_write to their own reach and no further. */
        /* Asking whether somebody has been here before, which is not
           attendance and not editing: it reads the faces of people who have
           left, so it is offered to whoever takes somebody on. See 0186. */
        path: 'face/who',
        element: (
          <Guarded permission="employees.create.temporary" alternative="employees.edit">
            <Lazy><FaceLookupPage /></Lazy>
          </Guarded>
        ),
      },
      {
        path: 'face/enrol',
        element: (
          <Guarded permission="employees.edit" alternative="employees.create.temporary">
            <Lazy><FaceEnrolPage /></Lazy>
          </Guarded>
        ),
      },
      {
        path: 'payroll',
        element: (
          <Guarded permission="payroll.view">
            <Lazy><PayrollPage /></Lazy>
          </Guarded>
        ),
      },
      {
        path: 'salary-summary',
        element: (
          <Guarded permission="payroll.view">
            <Lazy><SalarySummaryPage /></Lazy>
          </Guarded>
        ),
      },
      {
        path: 'projects',
        element: (
          <Guarded permission="projects.view">
            <Lazy><ProjectsPage /></Lazy>
          </Guarded>
        ),
      },
      {
        path: 'projects/:id',
        element: (
          <Guarded permission="projects.view">
            <Lazy><ProjectDetailPage /></Lazy>
          </Guarded>
        ),
      },
      {
        path: 'leaves',
        element: (
          <Guarded permission="leave.view">
            <Lazy><LeavesPage /></Lazy>
          </Guarded>
        ),
      },
      {
        path: 'leaves/:id',
        element: (
          <Guarded permission="leave.view">
            <Lazy><LeaveDetailPage /></Lazy>
          </Guarded>
        ),
      },
      {
        path: 'advances',
        element: (
          <Guarded permission="advances.view">
            <Lazy><AdvancesPage /></Lazy>
          </Guarded>
        ),
      },
      {
        path: 'fleet',
        element: (
          <Guarded permission="fleet.view">
            <Lazy><FleetPage /></Lazy>
          </Guarded>
        ),
      },
      {
        path: 'reports',
        element: (
          <Guarded permission="reports.export">
            <Lazy><ReportsPage /></Lazy>
          </Guarded>
        ),
      },
      {
        path: 'documents',
        element: (
          <Guarded permission="employees.view">
            <Lazy><DocumentsPage /></Lazy>
          </Guarded>
        ),
      },

      {
        path: 'settings',
        element: (
          <Guarded permission="settings.manage">
            <SettingsLayout />
          </Guarded>
        ),
        children: [
          { index: true, element: <Navigate to="/settings/appearance" replace /> },
          { path: 'appearance', element: <AppearanceSettings /> },
          { path: 'rules', element: <Lazy><RulesWizard /></Lazy> },
          { path: 'company', element: <Lazy><CompanySettings /></Lazy> },
          { path: 'holidays', element: <Lazy><HolidaySettings /></Lazy> },
          { path: 'branches', element: <Lazy><BranchSettings /></Lazy> },
          { path: 'users', element: <Lazy><UserSettings /></Lazy> },
          { path: 'security', element: <Lazy><SecuritySettings /></Lazy> },
          { path: 'backups', element: <Lazy><BackupSettings /></Lazy> },
          { path: 'sign-ins', element: <Lazy><LoginLog /></Lazy> },
          { path: 'activity', element: <Lazy><ActivityLog /></Lazy> },
          { path: 'email', element: <Lazy><EmailSettings /></Lazy> },
          { path: 'self-check', element: <Lazy><SelfCheck /></Lazy> },
          { path: 'import', element: <Lazy><ImportPage /></Lazy> },
          { path: 'notifications', element: <Lazy><NotificationSettings /></Lazy> },
          /* Deliberately absent from the settings navigation. Reached by typing
             the address, and refused to anybody but a Super Admin even then —
             the guard is in the database, this only keeps it out of the way of
             people who have no business finding it by clicking. */
          { path: 'permanent-delete', element: <Lazy><PermanentDelete /></Lazy> },
        ],
      },

          { path: '*', element: <Navigate to="/" replace /> },
        ],
      },
    ],
  },
]);
